import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import TrustBadge from './TrustBadge';
import StarRating from './StarRating';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const TopMatches = ({ postId, token }) => {
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (postId) {
      fetchMatches();
    }
  }, [postId]);

  const fetchMatches = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/business/posts/${postId}/matches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMatches(response.data.matches || []);
    } catch (error) {
      console.error('Failed to fetch matches:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card data-testid="top-matches-widget">
        <CardHeader>
          <CardTitle className="text-lg">Top 3 Matches</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-brand-muted">Loading matches...</div>
        </CardContent>
      </Card>
    );
  }

  if (matches.length === 0) {
    return (
      <Card data-testid="top-matches-widget">
        <CardHeader>
          <CardTitle className="text-lg">Top 3 Matches</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-brand-muted">No matches found yet</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="top-matches-widget">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <span className="text-2xl">🎯</span>
          Top 3 Matches Right Now
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {matches.map((match, idx) => (
            <div
              key={match.salesperson_id}
              data-testid="match-card"
              className="rounded-lg border border-white/10 bg-brand-bg p-4 hover:border-brand-gold/40 transition-colors cursor-pointer"
              onClick={() => navigate(`/salespeople/${match.salesperson_id}`)}
            >
              <div className="flex items-start gap-3">
                {/* Rank Badge */}
                <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                  idx === 0 ? 'bg-brand-gold text-black' :
                  idx === 1 ? 'bg-gray-400 text-black' :
                  'bg-orange-600 text-white'
                }`}>
                  {idx + 1}
                </div>

                {/* Avatar */}
                <div className="flex-shrink-0">
                  {match.salesperson_avatar ? (
                    <img
                      src={match.salesperson_avatar}
                      alt={match.salesperson_name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-brand-gold/20 flex items-center justify-center text-brand-gold font-bold">
                      {match.salesperson_name.charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="font-semibold text-brand-text">{match.salesperson_name}</div>
                    {match.badge && <TrustBadge badge={match.badge} />}
                  </div>

                  {match.review_count > 0 && (
                    <div className="mb-2">
                      <StarRating
                        rating={match.avg_rating}
                        count={match.review_count}
                        size={14}
                      />
                    </div>
                  )}

                  {/* Match Score */}
                  <div className="mb-2">
                    <div className="text-sm text-brand-gold font-medium">
                      Match Score: {match.score.toFixed(0)}/100
                    </div>
                  </div>

                  {/* Match Reasons */}
                  {match.match_reasons && match.match_reasons.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {match.match_reasons.slice(0, 3).map((reason, ridx) => (
                        <span
                          key={ridx}
                          className="text-xs px-2 py-1 rounded bg-brand-gold/10 text-brand-gold border border-brand-gold/20"
                        >
                          {reason}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default TopMatches;
